import { useState, useEffect } from 'react';
import { motion, Reorder } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  GripVertical,
  Eye,
  EyeOff,
  ExternalLink,
  Menu,
  Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultMenuItem = {
  label: '',
  path: '',
  icon: '',
  is_visible: true,
  is_external: false
};

export default function AdminMenu() {
  const { token } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(defaultMenuItem);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/menu/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenuItems(response.data);
    } catch (error) {
      // If no menu exists yet, use empty array
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/menu/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Menu item updated');
      } else {
        const newItem = {
          ...formData,
          order: menuItems.length
        };
        await axios.post(`${API_URL}/api/menu`, newItem, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Menu item added');
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      setFormData(defaultMenuItem);
      fetchMenu();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save menu item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      label: item.label,
      path: item.path,
      icon: item.icon || '',
      is_visible: item.is_visible,
      is_external: item.is_external || false
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/menu/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu item deleted');
      fetchMenu();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      await axios.put(`${API_URL}/api/menu/${item.id}`, 
        { is_visible: !item.is_visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(item.is_visible ? 'Menu item hidden' : 'Menu item visible');
      fetchMenu();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleReorder = async (newOrder) => {
    setMenuItems(newOrder);
    setHasChanges(true);
  };

  const saveOrder = async () => {
    try {
      const orderData = menuItems.map((item, index) => ({
        id: item.id,
        order: index
      }));
      await axios.post(`${API_URL}/api/menu/reorder`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu order saved');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  const seedDefaultMenu = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/menu/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchMenu();
    } catch (error) {
      toast.error('Failed to seed menu');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-menu-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Menu Management</h1>
          <p className="text-muted-foreground mt-1">Drag to reorder navigation items</p>
        </div>
        
        <div className="flex gap-2">
          {menuItems.length === 0 && (
            <Button variant="outline" onClick={seedDefaultMenu} data-testid="seed-menu-btn">
              <Download className="w-4 h-4 mr-2" />
              Load Default Menu
            </Button>
          )}
          
          {hasChanges && (
            <Button onClick={saveOrder} className="btn-gold" data-testid="save-order-btn">
              Save Order
            </Button>
          )}
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingItem(null);
              setFormData(defaultMenuItem);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-emerald" data-testid="add-menu-item-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Menu Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Label *</Label>
                  <Input
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    placeholder="e.g., About Us"
                    required
                    data-testid="menu-label-input"
                  />
                </div>

                <div>
                  <Label>Path / URL *</Label>
                  <Input
                    value={formData.path}
                    onChange={(e) => setFormData({...formData, path: e.target.value})}
                    placeholder="/about or https://..."
                    required
                    data-testid="menu-path-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use /path for internal pages or full URL for external links
                  </p>
                </div>

                <div>
                  <Label>Icon Name (optional)</Label>
                  <Input
                    value={formData.icon}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    placeholder="e.g., home, bed-double, utensils"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lucide icon name (optional)
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Visible</Label>
                    <p className="text-xs text-muted-foreground">Show in navigation</p>
                  </div>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({...formData, is_visible: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>External Link</Label>
                    <p className="text-xs text-muted-foreground">Opens in new tab</p>
                  </div>
                  <Switch
                    checked={formData.is_external}
                    onCheckedChange={(checked) => setFormData({...formData, is_external: checked})}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-emerald" data-testid="save-menu-item-btn">
                    {editingItem ? 'Update' : 'Add Item'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Preview */}
      <Card className="mb-8 bg-emerald-950 text-white">
        <CardContent className="p-4">
          <p className="text-xs text-emerald-300 mb-2">Live Preview</p>
          <div className="flex flex-wrap gap-4">
            {menuItems.filter(item => item.is_visible).map((item) => (
              <span key={item.id} className="text-sm hover:text-gold transition-colors cursor-pointer">
                {item.label}
                {item.is_external && <ExternalLink className="w-3 h-3 inline ml-1" />}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Menu Items List */}
      {menuItems.length > 0 ? (
        <Reorder.Group axis="y" values={menuItems} onReorder={handleReorder} className="space-y-2">
          {menuItems.map((item) => (
            <Reorder.Item key={item.id} value={item}>
              <Card 
                className={`cursor-grab active:cursor-grabbing ${!item.is_visible ? 'opacity-50' : ''}`}
                data-testid={`menu-item-${item.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {item.is_external && (
                        <Badge variant="outline" className="text-xs">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          External
                        </Badge>
                      )}
                      {!item.is_visible && (
                        <Badge variant="secondary" className="text-xs">Hidden</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{item.path}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleToggleVisibility(item)}
                    >
                      {item.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleEdit(item)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Menu className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No menu items yet</p>
          <Button variant="outline" onClick={seedDefaultMenu}>
            <Download className="w-4 h-4 mr-2" />
            Load Default Menu
          </Button>
        </div>
      )}
    </div>
  );
}
